import React from "react";
import { FieldLabeledProps, RichTextEditorProps } from "../../types/fieldTypes";
import { useRichTextEditorField } from "./useRichTextEditorField";
import {
  HtmlEditor,
  Inject,
  Link,
  PasteCleanup,
  QuickToolbar,
  RichTextEditorComponent,
  RichTextEditorModel,
  Toolbar,
} from "@syncfusion/ej2-react-richtexteditor";
import { FormGroup } from "@blueprintjs/core";
import classNames from "classnames";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base/src";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import {
  MentionComponent,
  MentionModel,
} from "@syncfusion/ej2-react-dropdowns";

export enum RichTextEditorValueType {
  html,
  base64,
}

export interface RichTextEditorFieldProps extends RichTextEditorProps {
  valueType: RichTextEditorValueType;
  richTextEditorSettings?: RichTextEditorModel & DefaultHtmlAttributes;
  showFullScreenOption?: boolean;
  labelProps?: FieldLabeledProps;
  getPlainTextOnSubmit?: (plainTextValue: string) => void;
  mentionProps?: MentionModel;
  enable?: {
    mention?: boolean;
  };
  readonly?: boolean;
  editorId?: string;
}

const RichTextEditorField: React.FC<RichTextEditorFieldProps> = (props) => {
  const { richTextEditorRef, localValue, richTextEditorSettings, ready } =
    useRichTextEditorField(props);

  // Use dynamic editorId to prevent conflicts when multiple RichTextEditor instances exist on the same page
  const editorId = props.editorId || "mention_integration";
  // Generate unique target selector for mention component to avoid targeting wrong editor
  const mentionTargetId = `#${editorId}_rte-edit-view`;

  return (
    <FormGroup
      className={classNames("ucl-base-input-rich-text-editor-field", {
        "has-required-background": props.isRequired && !localValue,
      })}
    >
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          fieldProps={{ ...props, value: localValue }}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      <div className="ucl-base-input-rich-text-editor-field-container">
        {ready && (
          <RichTextEditorComponent
            ref={richTextEditorRef}
            value={localValue}
            enabled={!props.disabled}
            readonly={props?.readOnly || false}
            {...richTextEditorSettings}
            id={editorId}
          >
            <Inject
              services={[Toolbar, Link, HtmlEditor, QuickToolbar, PasteCleanup]}
            />
          </RichTextEditorComponent>
        )}
      </div>
      {ready && !!richTextEditorRef.current && props.enable?.mention && (
        <MentionComponent
          id={`${editorId}_mention`}
          target={mentionTargetId}
          suggestionCount={8}
          showMentionChar={false}
          allowSpaces
          popupWidth="250px"
          popupHeight="200px"
          {...props.mentionProps}
        ></MentionComponent>
      )}
      {props.errorMessages && (
        <FormFieldErrorContainer errorMessages={props.errorMessages} />
      )}
    </FormGroup>
  );
};

export default RichTextEditorField;
